import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, Button, Modal, Form, Input, InputNumber, Tag, Space, message, Popconfirm, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { getDepots, createDepot, updateDepot, deleteDepot } from '../../services/adminService';

const { Title } = Typography;

const DepotListPage = () => {
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDepot, setEditingDepot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchDepots = async () => {
    setLoading(true);
    try {
      const data = await getDepots();
      setDepots(data.depots || []);
    } catch (err) {
      message.error('Erreur lors du chargement des dépôts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDepots(); }, []);

  const openCreate = () => {
    setEditingDepot(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (depot) => {
    setEditingDepot(depot);
    let depts = depot.departements;
    if (typeof depts === 'string') {
      try { depts = JSON.parse(depts); } catch { depts = []; }
    }
    form.setFieldsValue({
      nom: depot.nom,
      ville: depot.ville,
      adresse_complete: depot.adresse_complete,
      departements: Array.isArray(depts) ? depts.map(String) : [],
      lat: depot.lat,
      lng: depot.lng,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        departements: values.departements || [],
      };
      if (editingDepot) {
        await updateDepot(editingDepot.id, payload);
        message.success('Dépôt mis à jour');
      } else {
        await createDepot(payload);
        message.success('Dépôt créé');
      }
      setModalOpen(false);
      fetchDepots();
    } catch (err) {
      if (err.response?.data?.error) {
        message.error(err.response.data.error);
      } else if (!err.errorFields) {
        message.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDepot(id);
      message.success('Dépôt supprimé');
      fetchDepots();
    } catch (err) {
      message.error(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const columns = [
    {
      title: 'Nom', dataIndex: 'nom', key: 'nom',
      sorter: (a, b) => a.nom.localeCompare(b.nom),
    },
    { title: 'Ville', dataIndex: 'ville', key: 'ville' },
    { title: 'Adresse', dataIndex: 'adresse_complete', key: 'adresse', ellipsis: true },
    {
      title: 'Départements', dataIndex: 'departements', key: 'departements',
      render: (depts) => {
        let arr = depts;
        if (typeof arr === 'string') {
          try { arr = JSON.parse(arr); } catch { arr = []; }
        }
        if (!Array.isArray(arr)) return '-';
        return arr.map(d => <Tag key={d}>{d}</Tag>);
      },
    },
    {
      title: 'Équipes', dataIndex: 'nb_equipes', key: 'nb_equipes', width: 90,
      render: (n) => <Tag color={n > 0 ? 'blue' : 'default'}>{n}</Tag>,
    },
    {
      title: 'Coordonnées', key: 'coords', width: 120,
      render: (_, r) => r.lat && r.lng
        ? <Tag icon={<EnvironmentOutlined />} color="green">{Number(r.lat).toFixed(2)}, {Number(r.lng).toFixed(2)}</Tag>
        : <Tag color="orange">Non géocodé</Tag>,
    },
    {
      title: 'Actions', key: 'actions', width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Supprimer ce dépôt ?"
            description="Les équipes liées devront être réassignées au préalable."
            onConfirm={() => handleDelete(record.id)}
            okText="Supprimer"
            cancelText="Annuler"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Gestion des dépôts</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nouveau dépôt
        </Button>
      </div>

      <Card>
        <Table
          dataSource={depots}
          columns={columns}
          loading={loading}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20 }}
        />
      </Card>

      <Modal
        title={editingDepot ? 'Modifier le dépôt' : 'Nouveau dépôt'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        okText="Enregistrer"
        cancelText="Annuler"
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="nom" label="Nom du dépôt" rules={[{ required: true, message: 'Le nom est obligatoire' }]}>
            <Input placeholder="Ex: Dépôt Sud" />
          </Form.Item>
          <Form.Item name="ville" label="Ville" rules={[{ required: true, message: 'La ville est obligatoire' }]}>
            <Input placeholder="Ex: Marseille" />
          </Form.Item>
          <Form.Item name="adresse_complete" label="Adresse complète">
            <Input placeholder="Ex: 12 rue des Oliviers, 13001 Marseille" />
          </Form.Item>
          <Form.Item name="departements" label="Départements couverts">
            <Select
              mode="tags"
              placeholder="Tapez les codes départements (ex: 13, 84, 30)"
              tokenSeparators={[',', ' ']}
            />
          </Form.Item>
          <Space style={{ width: '100%' }}>
            <Form.Item name="lat" label="Latitude">
              <InputNumber step={0.0001} placeholder="43.2965" style={{ width: 180 }} />
            </Form.Item>
            <Form.Item name="lng" label="Longitude">
              <InputNumber step={0.0001} placeholder="5.3698" style={{ width: 180 }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default DepotListPage;
